import { useMemo, useState } from 'react';

import { useQueries } from '@tanstack/react-query';
import { Loader2, Play } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { useConfigurations } from '@/hooks/useConfigurations';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useCreatePipelineRun, usePipelineRuns } from '@/hooks/usePipelineRuns';
import { pipelineRunsApi } from '@/lib/api/pipeline-runs.api';
import { extractErrorMessage } from '@/lib/utils';
import { formatDateTime } from '@/utils/format';

import type { ImprovementSettings, PipelineRun } from '@/lib/api/pipeline-runs.api';

const statusColors: Record<string, string> = {
  QUEUED: 'bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-300',
  RUNNING: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  SUCCEEDED: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  PARTIAL: 'bg-yellow-100 text-yellow-800 dark:bg-amber-900/40 dark:text-amber-300',
  FAILED: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  CANCELLED: 'bg-gray-100 text-gray-500 dark:bg-gray-900/40 dark:text-gray-400',
  ROLLED_BACK: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
};

const autonomyColors: Record<string, string> = {
  off: 'bg-gray-100 text-gray-700 dark:bg-gray-900/40 dark:text-gray-300',
  propose: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  auto: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
};

/** Choices for how far back a manual run collects flagged calls. */
const WINDOW_DAY_OPTIONS = [1, 3, 7, 14, 30];

export const OverviewPage = () => {
  usePageTitle('Agent Improvement');
  const { data: configurations, isLoading: configsLoading } = useConfigurations();
  const { data: runs, isLoading: runsLoading } = usePipelineRuns();
  const createRun = useCreatePipelineRun();
  const { toast } = useToast();
  const [runningConfigId, setRunningConfigId] = useState<string | null>(null);
  // "Run now" options: how far back to look and how many flagged calls to review.
  const [windowDays, setWindowDays] = useState('1');
  const [maxCalls, setMaxCalls] = useState('10');

  // Fetch settings for each configuration to derive current autonomy level
  const configIds = useMemo(() => (configurations ?? []).map((c) => c.id), [configurations]);

  const settingsQueries = useQueries({
    queries: configIds.map((id) => ({
      queryKey: ['improvement-settings', id],
      queryFn: () => pipelineRunsApi.getSettings(id),
      enabled: !!id,
      staleTime: 60_000,
    })),
  });

  const settingsByConfig = useMemo(() => {
    const map: Record<string, ImprovementSettings> = {};
    configIds.forEach((id, i) => {
      const data = settingsQueries[i]?.data;
      if (data) map[id] = data;
    });
    return map;
  }, [configIds, settingsQueries]);

  const isLoading = configsLoading || runsLoading;

  const latestRunByConfig = (runs ?? []).reduce<Record<string, PipelineRun>>((acc, run) => {
    const existing = acc[run.configuration_id];
    if (!existing || new Date(run.created_at) > new Date(existing.created_at)) {
      acc[run.configuration_id] = run;
    }
    return acc;
  }, {});

  const handleRunNow = async (configurationId: string) => {
    try {
      setRunningConfigId(configurationId);
      const parsedMax = Number.parseInt(maxCalls, 10);
      await createRun.mutateAsync({
        configurationId,
        windowDays: Number.parseInt(windowDays, 10),
        maxCalls: Number.isFinite(parsedMax) && parsedMax > 0 ? Math.min(parsedMax, 100) : 10,
      });
      toast({
        variant: 'success',
        title: 'Pipeline run triggered',
        description: 'A new improvement run has been queued.',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to trigger run',
        description: extractErrorMessage(error),
      });
    } finally {
      setRunningConfigId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <CardTitle>Agent Improvement</CardTitle>
              <CardDescription>
                Overview of all configurations with their improvement pipeline status and autonomy
                level.
              </CardDescription>
            </div>
            <div className="flex items-end gap-3">
              <div className="space-y-1">
                <span className="text-muted-foreground text-[10px] font-semibold tracking-widest uppercase">
                  Review last
                </span>
                <Select value={windowDays} onValueChange={setWindowDays}>
                  <SelectTrigger className="h-8 w-28 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WINDOW_DAY_OPTIONS.map((d) => (
                      <SelectItem key={d} value={String(d)}>
                        {d === 1 ? '1 day' : `${d} days`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <span className="text-muted-foreground text-[10px] font-semibold tracking-widest uppercase">
                  Max calls
                </span>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={maxCalls}
                  onChange={(e) => setMaxCalls(e.target.value)}
                  className="h-8 w-20 text-xs"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!configurations?.length ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              No configurations found.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Configuration</TableHead>
                  <TableHead>Autonomy</TableHead>
                  <TableHead>Last Run</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Decision</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {configurations.map((config) => {
                  const lastRun = latestRunByConfig[config.id];
                  const settings = settingsByConfig[config.id];
                  const autonomy = settings?.autonomy_level ?? 'off';
                  const isTriggering = runningConfigId === config.id;
                  return (
                    <TableRow key={config.id}>
                      <TableCell>
                        <div>
                          <p className="text-sm font-medium">{config.name}</p>
                          <p className="text-muted-foreground font-mono text-xs">
                            {config.id.slice(0, 8)}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={autonomyColors[autonomy] ?? autonomyColors.off}
                        >
                          {autonomy}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {lastRun ? formatDateTime(lastRun.created_at) : 'Never'}
                      </TableCell>
                      <TableCell>
                        {lastRun ? (
                          <Badge
                            variant="secondary"
                            className={statusColors[lastRun.status] ?? statusColors.QUEUED}
                          >
                            {lastRun.status}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {lastRun?.stage === 'decide'
                          ? String(lastRun.summary?.decision ?? '-')
                          : (lastRun?.stage ?? '-')}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isTriggering}
                          onClick={() => handleRunNow(config.id)}
                        >
                          {isTriggering ? (
                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                          ) : (
                            <Play className="mr-1 h-3 w-3" />
                          )}
                          Run now
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
